import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { Modal } from "bootstrap"

const MAX_FILE_SIZE = 100 * 1024 * 1024
const ACCEPTED_EXTENSIONS = [".pdf", ".ppt", ".pptx"]
const MAX_LINK_LENGTH = 2048

export default class extends Controller {
  // Every `add*`/`edit*` target below is emitted from ONE view —
  // app/views/workplace/document_library/_resource_fields.html.erb — which builds the
  // names as `${mode}Title`, `browse${Mode}` and so on. Grepping a literal target name
  // through the views will therefore find nothing; rename them there, not just here.
  static targets = [
    "addModal", "addForm", "addTitle", "addDescription", "addTag", "addLink",
    "addFileInput", "addSignedId", "addFilename", "addSubmit", "addPreview",
    "addError", "addDropzone", "addFileRow", "addFileName", "addRole",
    "addKind", "addKindOption", "addBody", "editBody",
    "editModal", "editForm", "editTitle", "editDescription", "editTag", "editLink",
    "editFileInput", "editSignedId", "editFilename", "editSubmit", "editPreview",
    "editError", "editDropzone", "editFileRow", "editFileName", "editRole"
  ]
  static values = { directUploadUrl: String }

  connect() {
    this.addObjectUrl = null
    this.editObjectUrl = null
    this.editExistingPreviewUrl = ""
    this.editHasExistingFile = false
    this.editFileRemoved = false
    this.editInitialState = null
    this.submittedAdd = false
    this.submittedEdit = false
    this.uploadedFiles = {}
    // A member who may only add links gets a modal that starts (and stays) on Link
    // (TMS-1651); an admin's starts on File and can switch.
    this.initialAddKind = this.addKindTarget.value
    this.editKind = "file"

    this.boundAddHidden = () => this.resetAdd()
    this.boundEditHidden = () => this.resetEdit()
    this.addModalTarget.addEventListener("hidden.bs.modal", this.boundAddHidden)
    this.editModalTarget.addEventListener("hidden.bs.modal", this.boundEditHidden)
    this.refreshAddActions()
  }

  disconnect() {
    this.revokeObjectUrl("add")
    this.revokeObjectUrl("edit")
    this.addModalTarget.removeEventListener("hidden.bs.modal", this.boundAddHidden)
    this.editModalTarget.removeEventListener("hidden.bs.modal", this.boundEditHidden)
  }

  refreshAddActions() {
    const hasContent = this.isLink("add") ? Boolean(this.addLinkTarget.value.trim()) : Boolean(this.addFile())
    this.addSubmitTarget.disabled = !(this.addTitleTarget.value.trim() && hasContent)
    this.addPreviewTarget.disabled = !hasContent
  }

  refreshEditActions() {
    const hasContent = this.isLink("edit") ? Boolean(this.editLinkTarget.value.trim()) : this.editHasFile()
    const dirty = this.editState() !== this.editInitialState
    this.editSubmitTarget.disabled = !(dirty && this.editTitleTarget.value.trim() && hasContent)
    this.editPreviewTarget.disabled = !hasContent
  }

  // "Upload a file" / "Add a link" in Add resource. The two share every other field,
  // so switching keeps what was typed and only changes which of file or URL is asked for.
  selectKind(event) {
    event.preventDefault()
    this.applyKind("add", event.currentTarget.dataset.kind)
    this.hideError("add")
    this.refreshAddActions()
  }

  applyKind(mode, kind) {
    const link = kind === "link"
    if (mode === "add") {
      this.addKindTarget.value = link ? "link" : "file"
      this.addKindOptionTargets.forEach((option) => {
        const selected = option.dataset.kind === this.addKindTarget.value
        option.classList.toggle("segmented-control-option-selected", selected)
        option.setAttribute("aria-pressed", String(selected))
      })
    } else {
      this.editKind = link ? "link" : "file"
    }

    const body = mode === "add" ? this.addBodyTarget : this.editBodyTarget
    body.classList.toggle("is-link-kind", link)
  }

  isLink(mode) {
    return (mode === "add" ? this.addKindTarget.value : this.editKind) === "link"
  }

  browseAdd(event) {
    event.preventDefault()
    this.addFileInputTarget.click()
  }

  browseEdit(event) {
    event.preventDefault()
    this.editFileInputTarget.click()
  }

  addFileChanged() {
    this.applySelectedFile("add", this.addFileInputTarget.files[0])
  }

  editFileChanged() {
    this.applySelectedFile("edit", this.editFileInputTarget.files[0])
  }

  dropAdd(event) {
    this.dropOn(event, "add")
  }

  dropEdit(event) {
    this.dropOn(event, "edit")
  }

  dragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add("is-dragover")
  }

  dragLeave(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return
    event.currentTarget.classList.remove("is-dragover")
  }

  removeAddFile(event) {
    event.preventDefault()
    this.clearFile("add")
    this.refreshAddActions()
  }

  removeEditFile(event) {
    event.preventDefault()
    this.editFileRemoved = true
    this.editHasExistingFile = false
    this.clearFile("edit")
    this.showEditFile(false)
    this.refreshEditActions()
  }

  openEdit(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.resetEdit()

    this.editFormTarget.action = button.dataset.url
    this.applyKind("edit", button.dataset.kind)
    this.editTitleTarget.value = button.dataset.title || ""
    this.editDescriptionTarget.value = button.dataset.description || ""
    this.editTagTarget.value = button.dataset.resourceTag || ""
    this.editLinkTarget.value = button.dataset.linkUrl || ""
    this.editExistingPreviewUrl = button.dataset.previewUrl || ""
    this.editFilenameTarget.value = button.dataset.filename || ""
    this.editFileNameTarget.textContent = button.dataset.filename || ""
    this.editHasExistingFile = Boolean(button.dataset.filename)
    this.editFileRemoved = false

    const roles = (button.dataset.roles || "").split(" ").filter(Boolean)
    this.editRoleTargets.forEach((checkbox) => {
      checkbox.checked = roles.includes(checkbox.value)
    })

    this.showEditFile(this.editHasExistingFile)
    this.editInitialState = this.editState()
    this.refreshEditActions()
    Modal.getOrCreateInstance(this.editModalTarget).show()
  }

  // Both modals render the same chips, so the mode comes from which target list the
  // clicked checkbox belongs to rather than from a separate action per modal.
  toggleRole(event) {
    const mode = this.addRoleTargets.includes(event.currentTarget) ? "add" : "edit"

    if (this.selectedRoles(mode).length === 0) {
      event.currentTarget.checked = true
      this.showError(mode, "Choose who can see this resource.")
      return
    }

    this.hideError(mode)
    if (mode === "add") this.refreshAddActions()
    else this.refreshEditActions()
  }

  previewAdd(event) {
    event.preventDefault()
    if (this.isLink("add")) return this.previewLink("add")

    this.openPreview(this.addObjectUrl)
  }

  previewEdit(event) {
    event.preventDefault()
    if (this.isLink("edit")) return this.previewLink("edit")

    this.openPreview(this.editObjectUrl || this.editExistingPreviewUrl)
  }

  // The preview of a link is the page itself, once the URL passes the same rule the
  // server applies.
  previewLink(mode) {
    const error = this.linkError(mode)
    if (error) {
      this.showError(mode, error)
      return
    }

    this.hideError(mode)
    this.openPreview((mode === "add" ? this.addLinkTarget : this.editLinkTarget).value.trim())
  }

  submitAdd(event) {
    event.preventDefault()
    if (this.submittedAdd) return

    this.hideError("add")

    if (this.isLink("add")) {
      this.submitAddLink()
      return
    }

    const file = this.addFile()
    if (!this.addTitleTarget.value.trim() || !file) {
      this.showError("add", "Enter a title and attach a file to publish.")
      return
    }

    if (this.selectedRoles("add").length === 0) {
      this.showError("add", "Choose who can see this resource.")
      return
    }

    const linkError = this.linkError("add")
    if (linkError) {
      this.showError("add", linkError)
      return
    }

    this.uploadAndSubmit("add", file)
  }

  submitAddLink() {
    if (!this.addTitleTarget.value.trim() || !this.addLinkTarget.value.trim()) {
      this.showError("add", "Enter a URL and a title to add the link.")
      return
    }

    if (this.selectedRoles("add").length === 0) {
      this.showError("add", "Choose who can see this resource.")
      return
    }

    const linkError = this.linkError("add")
    if (linkError) {
      this.showError("add", linkError)
      return
    }

    this.postForm("add")
  }

  submitEdit(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.submittedEdit) return

    this.hideError("edit")

    if (!this.editTitleTarget.value.trim()) {
      this.showError("edit", "A title is required.")
      return
    }

    if (this.isLink("edit") && !this.editLinkTarget.value.trim()) {
      this.showError("edit", "Enter a link.")
      return
    }

    if (!this.isLink("edit") && !this.editHasFile()) {
      this.showError("edit", "Attach a PDF or PPTX file.")
      return
    }

    if (this.selectedRoles("edit").length === 0) {
      this.showError("edit", "Choose who can see this resource.")
      return
    }

    const linkError = this.linkError("edit")
    if (linkError) {
      this.showError("edit", linkError)
      return
    }

    const file = this.editFile()
    if (file) {
      this.uploadAndSubmit("edit", file)
      return
    }

    this.postForm("edit")
  }

  applySelectedFile(mode, file) {
    const dropzone = mode === "add" ? this.addDropzoneTarget : this.editDropzoneTarget
    dropzone.classList.remove("is-dragover")

    if (!file) return

    const error = this.fileError(file)
    if (error) {
      this.showError(mode, error)
      this.clearFileInput(mode)
      return
    }

    this.hideError(mode)
    this.revokeObjectUrl(mode)
    const objectUrl = URL.createObjectURL(file)

    if (mode === "add") {
      this.addObjectUrl = objectUrl
      this.addFilenameTarget.value = file.name
      this.addFileNameTarget.textContent = file.name
      this.addDropzoneTarget.hidden = true
      this.addFileRowTarget.hidden = false
      this.refreshAddActions()
      return
    }

    this.editObjectUrl = objectUrl
    this.editFileRemoved = false
    this.editHasExistingFile = true
    this.editFilenameTarget.value = file.name
    this.editFileNameTarget.textContent = file.name
    this.showEditFile(true)
    this.refreshEditActions()
  }

  dropOn(event, mode) {
    event.preventDefault()
    const file = event.dataTransfer?.files?.[0]
    const input = mode === "add" ? this.addFileInputTarget : this.editFileInputTarget
    if (!file) return

    const transfer = new DataTransfer()
    transfer.items.add(file)
    input.files = transfer.files
    this.applySelectedFile(mode, file)
  }

  uploadAndSubmit(mode, file) {
    const submitButton = mode === "add" ? this.addSubmitTarget : this.editSubmitTarget
    const signedId = mode === "add" ? this.addSignedIdTarget : this.editSignedIdTarget
    submitButton.disabled = true

    // A rejected save leaves the chosen file in place so the admin can correct another
    // field and retry. Uploading it a second time would orphan the first blob.
    if (signedId.value && this.uploadedFiles[mode] === file) {
      this.postForm(mode)
      return
    }

    const upload = new DirectUpload(file, this.directUploadUrlValue)
    upload.create((error, blob) => {
      if (error) {
        submitButton.disabled = false
        this.showError(mode, "The file could not be uploaded. Try again.")
        return
      }

      const filename = mode === "add" ? this.addFilenameTarget : this.editFilenameTarget
      signedId.value = blob.signed_id
      filename.value = blob.filename
      this.uploadedFiles[mode] = file
      this.postForm(mode)
    })
  }

  // Posted with fetch rather than a native form submit so a rejection can be shown in
  // the modal the admin is working in, with everything they typed still there. A native
  // submit navigates the page away and throws the entries out (TMS-1464).
  async postForm(mode) {
    const form = mode === "add" ? this.addFormTarget : this.editFormTarget
    const submitButton = mode === "add" ? this.addSubmitTarget : this.editSubmitTarget

    this.markSubmitting(mode, true)
    submitButton.disabled = true

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken() }
      })
      const payload = await response.json().catch(() => ({}))

      if (response.ok && payload.redirect_url) {
        window.location.href = payload.redirect_url
        return
      }

      this.showFieldErrors(mode, payload.field_errors)
      this.showError(mode, (payload.errors || ["That change could not be saved. Try again."]).join(" "))
    } catch {
      this.showError(mode, "That change could not be saved. Try again.")
    }

    this.markSubmitting(mode, false)
    if (mode === "add") this.refreshAddActions()
    else this.refreshEditActions()
  }

  markSubmitting(mode, submitting) {
    if (mode === "add") this.submittedAdd = submitting
    else this.submittedEdit = submitting
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  fileError(file) {
    if (!this.isAcceptedFile(file)) return "Upload a PDF or PPTX file."
    if (file.size > MAX_FILE_SIZE) return "File must be 100 MB or smaller."
    return null
  }

  isAcceptedFile(file) {
    const name = (file.name || "").toLowerCase()
    return ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  }

  addFile() {
    return this.addFileInputTarget.files[0]
  }

  editFile() {
    return this.editFileInputTarget.files[0]
  }

  editHasFile() {
    return Boolean(this.editFile() || (this.editHasExistingFile && !this.editFileRemoved))
  }

  selectedRoles(mode) {
    const checkboxes = mode === "add" ? this.addRoleTargets : this.editRoleTargets
    return checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value)
  }

  // Mirrors the server's rule (Workplace::CompanyDocumentDetails) so a typo is caught
  // before the file is uploaded and the redirect throws away everything else typed.
  linkError(mode) {
    const field = mode === "add" ? this.addLinkTarget : this.editLinkTarget
    const value = field.value.trim()
    if (!value) return this.isLink(mode) ? "Enter a link." : null
    if (value.length > MAX_LINK_LENGTH) return "Links must be 2,048 characters or fewer."

    let url
    try {
      url = new URL(value)
    } catch {
      return /^[^\s:/]+\./.test(value) ? "Enter the full link, starting with https://" : "Enter a valid link."
    }

    if (!["http:", "https:"].includes(url.protocol) || !url.hostname) return "Enter a valid link."
    if (url.username || url.password) return "Links cannot include a username or password."

    return null
  }

  editState() {
    return JSON.stringify({
      title: this.editTitleTarget.value.trim(),
      description: this.editDescriptionTarget.value.trim(),
      tag: this.editTagTarget.value.trim(),
      link: this.editLinkTarget.value.trim(),
      roles: this.selectedRoles("edit").slice().sort(),
      kind: this.editKind,
      filename: this.editFilenameTarget.value,
      signedId: this.editSignedIdTarget.value,
      removed: this.editFileRemoved
    })
  }

  showEditFile(hasFile) {
    this.editFileRowTarget.hidden = !hasFile
    this.editDropzoneTarget.hidden = hasFile
  }

  clearFile(mode) {
    this.clearFileInput(mode)
    this.revokeObjectUrl(mode)
    delete this.uploadedFiles[mode]

    if (mode === "add") {
      this.addSignedIdTarget.value = ""
      this.addFilenameTarget.value = ""
      this.addFileNameTarget.textContent = ""
      this.addDropzoneTarget.hidden = false
      this.addFileRowTarget.hidden = true
      return
    }

    this.editSignedIdTarget.value = ""
    this.editFilenameTarget.value = ""
    this.editFileNameTarget.textContent = ""
    this.editExistingPreviewUrl = ""
  }

  clearFileInput(mode) {
    const input = mode === "add" ? this.addFileInputTarget : this.editFileInputTarget
    input.value = ""
  }

  resetAdd() {
    this.submittedAdd = false
    this.addFormTarget.reset()
    this.applyKind("add", this.initialAddKind)
    this.clearFile("add")
    this.hideError("add")
    this.refreshAddActions()
  }

  resetEdit() {
    this.submittedEdit = false
    this.editFormTarget.reset()
    this.editFileRemoved = false
    this.editHasExistingFile = false
    this.editExistingPreviewUrl = ""
    this.editInitialState = null
    this.clearFile("edit")
    this.showEditFile(false)
    this.hideError("edit")
    this.refreshEditActions()
  }

  openPreview(url) {
    if (!url) return
    window.open(url, "_blank", "noopener,noreferrer")
  }

  revokeObjectUrl(mode) {
    if (mode === "add" && this.addObjectUrl) {
      URL.revokeObjectURL(this.addObjectUrl)
      this.addObjectUrl = null
    }

    if (mode === "edit" && this.editObjectUrl) {
      URL.revokeObjectURL(this.editObjectUrl)
      this.editObjectUrl = null
    }
  }

  showError(mode, message) {
    const target = mode === "add" ? this.addErrorTarget : this.editErrorTarget
    target.textContent = message
    target.hidden = false
    // The modal scrolls, so the banner at the top can sit well out of view.
    target.scrollIntoView({ block: "nearest" })
  }

  hideError(mode) {
    const target = mode === "add" ? this.addErrorTarget : this.editErrorTarget
    target.hidden = true
    target.textContent = ""
    this.clearFieldErrors(mode)
  }

  // Marks the input the server objected to, using the `has-error` state the shared
  // input component already styles, so the admin can see which field to fix.
  showFieldErrors(mode, fieldErrors) {
    this.clearFieldErrors(mode)
    if (!fieldErrors) return

    Object.keys(fieldErrors).forEach((field) => {
      const input = this.fieldInput(mode, field)
      input?.closest(".app-input-group")?.classList.add("has-error")
    })
  }

  clearFieldErrors(mode) {
    const modal = mode === "add" ? this.addModalTarget : this.editModalTarget
    modal.querySelectorAll(".app-input-group.has-error").forEach((group) => {
      group.classList.remove("has-error")
    })
  }

  fieldInput(mode, field) {
    const inputs = {
      title: mode === "add" ? this.addTitleTarget : this.editTitleTarget,
      description: mode === "add" ? this.addDescriptionTarget : this.editDescriptionTarget,
      resource_tag: mode === "add" ? this.addTagTarget : this.editTagTarget,
      link_url: mode === "add" ? this.addLinkTarget : this.editLinkTarget
    }
    return inputs[field]
  }
}
